import React, { useState, useEffect, useMemo } from 'react';
import { useLayout } from '../context/LayoutContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Autocomplete,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  Add,
  CalendarToday,
  People,
  DateRange,
  NavigateNext,
  Edit,
  Delete,
  CheckCircle,
  PlayArrow,
  Refresh,
  Visibility,
  Download,
  Print,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';

import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Import hooks
import {
  useCreateRollingRosterMutation,
  useGetRollingRostersQuery,
  useUpdateRollingRosterMutation,
  useGenerateAvailabilitiesMutation,
  useUpdateRollingRosterPeriodMutation, // Add this
   useGenerateAvailabilitiesForPeriodMutation,  // Add this
  useSetNextGenerationDateMutation,             // Add this
  useDeleteRollingRosterMutation
} from '../slices/rollingRosterApiSlice';
import { useGetUsersQuery } from '../slices/usersApiSlice';

import ReactDOM from 'react-dom';
import RollingRosterPrintView from './RollingRosterPrintView';

// Import availability components for reuse
import AvailabilityDialog from './AvailabilityDialog';

const RollingRosterView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  
  // State
  const [startDate, setStartDate] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [viewMode, setViewMode] = useState('create'); // 'create' or 'edit'
  
  // Add state for next period
  const [nextPeriodDialogOpen, setNextPeriodDialogOpen] = useState(false);
const [nextStartDate, setNextStartDate] = useState(null);
const [generatePeriodDialogOpen, setGeneratePeriodDialogOpen] = useState(false);
const [selectedPeriodStartDate, setSelectedPeriodStartDate] = useState(null);

// Add to your existing state variables
const [printDialogOpen, setPrintDialogOpen] = useState(false);
const [isPrinting, setIsPrinting] = useState(false);

// Add mutation hooks
const [setNextGenerationDate, { isLoading: settingNextDate }] = useSetNextGenerationDateMutation();
const [generateAvailabilitiesForPeriod, { isLoading: generatingPeriod }] = useGenerateAvailabilitiesForPeriodMutation();

// Add state
const [updatePeriodDialogOpen, setUpdatePeriodDialogOpen] = useState(false);
const [newPeriodStartDate, setNewPeriodStartDate] = useState(null);

  // Week and day constants
  const weeks = [1, 2, 3, 4, 5, 6];
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // Helper function to format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Helper function to parse date from input
  const parseDateFromInput = (dateString) => {
    return new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
  };
  
  // Calculate week dates - FIXED: Adjusts start to Monday
  // Calculate week dates - FIXED: Use selectedRoster.startDate when in edit mode
const weekDates = useMemo(() => {
  // Determine which date to use for calculation
  let dateToUse;
  
  if (viewMode === 'edit' && selectedRoster && selectedRoster.startDate) {
    // Use the roster's start date when editing
    dateToUse = new Date(selectedRoster.startDate);
  } else {
    // Use the state startDate when creating new or no roster selected
    dateToUse = new Date(startDate);
  }
  
  if (!dateToUse || isNaN(dateToUse.getTime())) {
    console.warn('Invalid date for week calculation');
    return [];
  }
  
  const dates = [];
  
  // Adjust to Monday if not already Monday (0=Sunday, 1=Monday, etc.)
  const dayOfWeek = dateToUse.getDay();
  const daysToMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 1 - dayOfWeek);
  const mondayDate = new Date(dateToUse);
  mondayDate.setDate(mondayDate.getDate() + daysToMonday);
  
  for (let week = 0; week < 6; week++) {
    const weekStart = new Date(mondayDate);
    weekStart.setDate(weekStart.getDate() + (week * 7));
    
    const weekDatesArray = [];
    for (let day = 0; day < 7; day++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + day);
      
      // Get day name
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayIndex = date.getDay();
      const dayKey = days[dayIndex === 0 ? 6 : dayIndex - 1]?.key || 'monday'; // Map to our days array
      
      weekDatesArray.push({
        date: date.toISOString().split('T')[0],
        display: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        dayIndex: dayIndex,
        dayKey: dayKey
      });
    }
    
    dates.push({
      weekNumber: week + 1,
      startDate: weekStart.toISOString().split('T')[0],
      dates: weekDatesArray
    });
  }
  
  return dates;
}, [startDate, selectedRoster, viewMode]); // Add viewMode and selectedRoster to dependencies
  
  // Get the date for a specific week and day
  const getDateForWeekDay = (weekNumber, dayKey) => {
    if (!weekDates[weekNumber - 1]) return null;
    
    const weekData = weekDates[weekNumber - 1];
    const dayIndex = days.findIndex(d => d.key === dayKey);
    if (dayIndex === -1) return null;
    
    const weekStart = new Date(weekData.startDate);
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex);
    
    return {
      date: date.toISOString().split('T')[0],
      display: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    };
  };
  
  // API Hooks
  const { data: staffData, isLoading: loadingStaff } = useGetUsersQuery();
  const { data: rostersData, isLoading: loadingRosters, refetch: refetchRosters } = useGetRollingRostersQuery({
    page: 1,
    limit: 100
  });
  
  const [createRollingRoster, { isLoading: creating }] = useCreateRollingRosterMutation();
  const [updateRollingRoster, { isLoading: updating }] = useUpdateRollingRosterMutation();
  const [generateAvailabilities, { isLoading: generating }] = useGenerateAvailabilitiesMutation();
  const [deleteRollingRoster, { isLoading: deleting }] = useDeleteRollingRosterMutation();

  // Add mutation hook
const [updateRollingRosterPeriod, { isLoading: updatingPeriod }] = useUpdateRollingRosterPeriodMutation();

  
  // Layout effect
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);
  
  // Process staff data
  useEffect(() => {
    if (staffData) {
      const filtered = staffData.filter(user => !user.isAdmin && !user.isAgency);
      setStaffList(filtered);
      setFilteredStaff(filtered);
    }
  }, [staffData]);
  
  // Apply job title filter
  useEffect(() => {
    if (selectedJobTitleFilter === 'all') {
      setFilteredStaff(staffList);
    } else {
      const filtered = staffList.filter(staff => 
        staff.jobtitle === selectedJobTitleFilter
      );
      setFilteredStaff(filtered);
    }
  }, [selectedJobTitleFilter, staffList]);
  
  // Job titles for filter
  const jobTitles = useMemo(() => {
    const titles = [...new Set(staffList.map(staff => staff.jobtitle).filter(Boolean))].sort();
    return ['all', ...titles];
  }, [staffList]);
  
  // Handle create rolling roster
  const handleCreateRollingRoster = async () => {
    try {
      if (selectedStaff.length === 0) {
        showSnackbar('Please select at least one staff member', 'error');
        return;
      }
      
      const result = await createRollingRoster({
        startDate: startDate.toISOString(),
        staffList: selectedStaff.map(staff => staff._id),
        createdBy: userInfo._id
      }).unwrap();
      
      showSnackbar(`Created ${result.data.createdRosters.length} rolling rosters`, 'success');
      setCreateDialogOpen(false);
      setSelectedStaff([]);
      refetchRosters();
      
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to create rolling rosters', 'error');
    }
  };
  
  // Function to handle updating the period
const handleUpdatePeriod = async () => {
  try {
    if (!selectedRoster || !newPeriodStartDate) {
      showSnackbar('Please select a new start date for the period', 'error');
      return;
    }
    
    const result = await updateRollingRosterPeriod({
      id: selectedRoster._id,
      newStartDate: newPeriodStartDate.toISOString()
    }).unwrap();
    
    showSnackbar('Rolling roster period updated successfully', 'success');
    setUpdatePeriodDialogOpen(false);
    setNewPeriodStartDate(null);
    
    // Update local state
    if (result.data.roster) {
      setSelectedRoster(result.data.roster);
    }
    
    refetchRosters();
    
  } catch (error) {
    showSnackbar(error?.data?.message || 'Failed to update period', 'error');
  }
};

  // Handle edit availability for a specific day
  const handleEditDayAvailability = (roster, weekNumber, dayKey) => {
    setSelectedRoster(roster);
    setSelectedWeek(weekNumber);
    setSelectedDay(dayKey);
    setViewMode('edit');
    
    // Get existing data if any
    const weekKey = `week${weekNumber}`;
    const existingData = roster[weekKey]?.[dayKey] || null;
    
    // Get the actual date for this day
    const dateInfo = getDateForWeekDay(weekNumber, dayKey);
    
    setSelectedDayData({
      ...existingData,
      week: weekNumber,
      day: dayKey,
      date: dateInfo?.date || '',
      dateDisplay: dateInfo?.display || '',
      staffId: roster.employeeId._id || roster.employeeId,
      staffName: roster.employeeName,
      jobTitle: roster.jobTitle,
      ward: roster.ward
    });
    
    setAvailabilityDialogOpen(true);
  };
  
  // Handle save availability
 // Handle save availability
const handleSaveAvailability = async (availabilityData) => {
  try {
    if (!selectedRoster) return;
    
    const weekKey = `week${selectedWeek}`;
    const updateData = {
      [weekKey]: {
        ...selectedRoster[weekKey],
        [selectedDay]: availabilityData
      }
    };
    
    // Perform optimistic update
    const updatedRoster = {
      ...selectedRoster,
      [weekKey]: {
        ...selectedRoster[weekKey],
        [selectedDay]: availabilityData
      }
    };
    setSelectedRoster(updatedRoster);
    
    // Update in backend
    await updateRollingRoster({
      id: selectedRoster._id,
      ...updateData
    }).unwrap();
    
    showSnackbar('Availability updated successfully', 'success');
    setAvailabilityDialogOpen(false);
    
    // Force refetch of roster data
    refetchRosters();
    
  } catch (error) {
    // Revert on error
    showSnackbar(error?.data?.message || 'Failed to update availability', 'error');
    // Re-fetch original data
    refetchRosters();
  }
};
  
  // Handle generate availabilities
  const handleGenerateAvailabilities = async (rosterId) => {
    try {
      const result = await generateAvailabilities({
        id: rosterId,
        createdBy: userInfo._id
      }).unwrap();
      
      showSnackbar(
        `Generated ${result.data.createdCount} new and updated ${result.data.updatedCount} existing availabilities`,
        'success'
      );
      
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to generate availabilities', 'error');
    }
  };
  
  // Handle delete roster
  const handleDeleteRoster = async (rosterId) => {
    if (window.confirm('Are you sure you want to delete this rolling roster?')) {
      try {
        await deleteRollingRoster(rosterId).unwrap();
        showSnackbar('Rolling roster deleted successfully', 'success');
        refetchRosters();
      } catch (error) {
        showSnackbar(error?.data?.message || 'Failed to delete rolling roster', 'error');
      }
    }
  };
  
  // Helper functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  

  // Function to handle setting next generation date
const handleSetNextGenerationDate = async () => {
  try {
    if (!selectedRoster || !nextStartDate) {
      showSnackbar('Please select a start date for the next period', 'error');
      return;
    }
    
    const result = await setNextGenerationDate({
      id: selectedRoster._id,
      nextStartDate: nextStartDate.toISOString()
    }).unwrap();
    
    showSnackbar('Next generation date set successfully', 'success');
    setNextPeriodDialogOpen(false);
    setNextStartDate(null);
    refetchRosters();
    
  } catch (error) {
    showSnackbar(error?.data?.message || 'Failed to set next generation date', 'error');
  }
};

// Function to generate availabilities for a specific period
const handleGenerateForPeriod = async (periodStartDate) => {
  try {
    if (!selectedRoster || !periodStartDate) {
      showSnackbar('Please select a period start date', 'error');
      return;
    }
    
    const result = await generateAvailabilitiesForPeriod({
      id: selectedRoster._id,
      startDate: periodStartDate.toISOString(),
      createdBy: userInfo._id
    }).unwrap();
    
    showSnackbar(
      `Generated ${result.data.createdCount} availabilities for period ${result.data.period.display}`,
      'success'
    );
    
    setGeneratePeriodDialogOpen(false);
    setSelectedPeriodStartDate(null);
    refetchRosters();
    
  } catch (error) {
    showSnackbar(error?.data?.message || 'Failed to generate availabilities for period', 'error');
  }
};


  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  const getDayDisplay = (roster, weekNumber, dayKey) => {
  // Add null check for roster
  if (!roster) {
    return (
      <Box sx={{ textAlign: 'center', color: '#999', fontStyle: 'italic', fontSize: '0.8rem' }}>
        No roster data
      </Box>
    );
  }
  
  const weekKey = `week${weekNumber}`;
  const dayData = roster[weekKey]?.[dayKey];
  
  if (!dayData || dayData === null) {
    return (
      <Box sx={{ textAlign: 'center', color: '#999', fontStyle: 'italic', fontSize: '0.8rem' }}>
        Not set
      </Box>
    );
  }
  
  const isUnavailable = dayData.isUnavailable || dayData.isSickLeave;
  
  return (
    <Tooltip title={isUnavailable ? 
      `Unavailable: ${dayData.unavailabilityReason || dayData.sicknessReason || 'No reason'}` : 
      `Available: ${dayData.startTime || '08:00'} - ${dayData.endTime || '16:00'}`
    }>
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: isUnavailable ? 
            (dayData.isSickLeave ? '#f44336' : '#d32f2f') : 
            (dayData.status === 'Approved' ? '#4caf50' : '#2196f3'),
          color: 'white',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.9
          }
        }}
        onClick={() => handleEditDayAvailability(roster, weekNumber, dayKey)}
      >
        {isUnavailable ? '⛔' : '✓'} {dayData.startTime || '--'} - {dayData.endTime || '--'}
      </Box>
    </Tooltip>
  );
};
  
  // Format date display for roster period
  // Format date display for roster period
const formatRosterPeriod = (roster) => {
  if (!roster || !roster.startDate || !roster.endDate) {
    return 'Period not set';
  }
  
  try {
    const start = new Date(roster.startDate);
    const end = new Date(roster.endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Invalid period dates';
    }
    
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  } catch (error) {
    console.error('Error formatting roster period:', error);
    return 'Error formatting period';
  }
};
  
  if (loadingStaff) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <MuiLink
          component={RouterLink}
          to="/dashboard"
          underline="hover"
          color="inherit"
        >
          Dashboard
        </MuiLink>
        <Typography color="text.primary">6-Week Rolling Roster</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          6-Week Rolling Availability Roster
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            bgcolor: '#1976d2',
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          Create New Roster
        </Button>
      </Box>
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Manage Rosters" />
          <Tab label="Create New" />
        </Tabs>
      </Box>
      
      {/* Manage Rosters Tab */}
      {activeTab === 0 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Existing Rolling Rosters
          </Typography>
          
          {loadingRosters ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : rostersData?.data?.length === 0 ? (
            <Alert severity="info">
              No rolling rosters found. Create a new one to get started.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
  <TableRow>
    <TableCell>Staff Member</TableCell>
    <TableCell>Job Title</TableCell>
    <TableCell>Start Date</TableCell>  {/* This shows current period start */}
    <TableCell>End Date</TableCell>    {/* This shows current period end */}
    <TableCell>Next Generation</TableCell> {/* This shows scheduled next start */}
    <TableCell>Generated Periods</TableCell>
    <TableCell align="right">Actions</TableCell>
  </TableRow>
</TableHead>
                <TableBody>
                  {rostersData?.data?.map((roster) => (
                    <TableRow key={roster._id}>
                      <TableCell>
                        <Typography fontWeight="bold">{roster.employeeName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {roster.employeeEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>{roster.jobTitle}</TableCell>
                      <TableCell>
                        {new Date(roster.startDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(roster.endDate).toLocaleDateString()}
                      </TableCell>

                      <TableCell>
                      {roster.nextGenerationStartDate ? (
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {new Date(roster.nextGenerationStartDate).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Scheduled
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Not scheduled
                        </Typography>
                      )}
                    </TableCell>

                      <TableCell>
                        {/* Calculate how many weeks have data */}
                        {[1, 2, 3, 4, 5, 6].map(week => {
                          const weekKey = `week${week}`;
                          const hasData = Object.values(roster[weekKey] || {}).some(val => val !== null);
                          return (
                            <Chip
                              key={week}
                              label={`W${week}`}
                              size="small"
                              color={hasData ? 'success' : 'default'}
                              variant={hasData ? 'filled' : 'outlined'}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          );
                        })}
                      </TableCell>
                      
<TableCell>
  {roster.nextGenerationStartDate ? (
    <Box>
      <Typography variant="body2" fontWeight="bold">
        {new Date(roster.nextGenerationStartDate).toLocaleDateString()}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Scheduled
      </Typography>
    </Box>
  ) : (
    <Typography variant="body2" color="textSecondary">
      Not scheduled
    </Typography>
  )}
</TableCell>

<TableCell>
  {roster.generatedPeriods?.length || 0} periods
</TableCell>
                      <TableCell>
                        {roster.createdBy?.name || 'System'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>

                          <Tooltip title="Print Schedule">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedRoster(roster);
                              setPrintDialogOpen(true);
                            }}
                          >
                            <Print fontSize="small" />
                          </IconButton>
                        </Tooltip>

                          <Tooltip title="View/Edit Roster">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedRoster(roster);
                                setViewMode('edit');
                                setActiveTab(1);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Generate Availabilities">
                            <IconButton
                              size="small"
                              onClick={() => handleGenerateAvailabilities(roster._id)}
                              disabled={generating}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete Roster">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRoster(roster._id)}
                              disabled={deleting}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
      
      {/* Create/Edit Tab */}
      {activeTab === 1 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          {viewMode === 'edit' && selectedRoster ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6">
                    Editing Roster for: {selectedRoster.employeeName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedRoster.jobTitle} • {selectedRoster.ward || 'No Ward'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Period: {formatRosterPeriod(selectedRoster)}
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={() => handleGenerateAvailabilities(selectedRoster._id)}
                  disabled={generating}
                  sx={{
                    bgcolor: '#4caf50',
                    '&:hover': {
                      bgcolor: '#388e3c'
                    }
                  }}
                >
                  Generate Availabilities
                </Button>

                
<Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
  
<Button
  variant="outlined"
  startIcon={<CalendarToday />}
  onClick={() => {
    // Open a dialog to choose between setting next date or updating current period
    setNewPeriodStartDate(new Date());
    setUpdatePeriodDialogOpen(true);
  }}
>
  Update Period
</Button>
  
  <Button
    variant="outlined"
    startIcon={<PlayArrow />}
    onClick={() => {
      setSelectedPeriodStartDate(new Date());
      setGeneratePeriodDialogOpen(true);
    }}
  >
    Generate for Period
  </Button>

   {/* Add Print Button */}
  <Button
    variant="outlined"
    startIcon={<Print />}
    onClick={() => setPrintDialogOpen(true)}
    disabled={!selectedRoster}
  >
    Print Schedule
  </Button>

</Box>
              </Box>
              
              {/* Week Navigation */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton
                  onClick={() => setSelectedWeek(prev => Math.max(1, prev - 1))}
                  disabled={selectedWeek === 1}
                >
                  <ArrowBack />
                </IconButton>
                
                <Typography variant="h5" fontWeight="bold">
                  Week {selectedWeek}
                </Typography>
                
                <IconButton
                  onClick={() => setSelectedWeek(prev => Math.min(6, prev + 1))}
                  disabled={selectedWeek === 6}
                >
                  <ArrowForward />
                </IconButton>
              </Box>
              
              {/* Week Dates Display */}
              {weekDates[selectedWeek - 1] && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Week {selectedWeek} Dates:
                  </Typography>
                  <Grid container spacing={1}>
                    {weekDates[selectedWeek - 1].dates.map((dateInfo, index) => (
                      <Grid item xs key={dateInfo.date}>
                        <Card sx={{ textAlign: 'center', p: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {days.find(d => d.key === dateInfo.dayKey)?.label || 'Day'}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {dateInfo.display}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              {/* Week Grid */}
              <Grid container spacing={2}>
                {days.map((day) => {
                  const dateInfo = getDateForWeekDay(selectedWeek, day.key);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={1.7} key={day.key}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 3,
                            borderColor: 'primary.main'
                          }
                        }}
                        onClick={() => handleEditDayAvailability(selectedRoster, selectedWeek, day.key)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                            {day.label}
                          </Typography>
                          
                          {dateInfo && (
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                              {dateInfo.display}
                            </Typography>
                          )}
                          
                          {getDayDisplay(selectedRoster, selectedWeek, day.key)}
                          
                          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            Click to edit
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              
              {/* Week Selector */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1 }}>
                {weeks.map(week => (
                  <Button
                    key={week}
                    variant={selectedWeek === week ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setSelectedWeek(week)}
                  >
                    Week {week}
                  </Button>
                ))}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Create New 6-Week Rolling Roster
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                This will create a 6-week rolling availability roster starting from the selected date.
                You can then set availability/unavailability for each day of each week.
              </Alert>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={formatDateForInput(startDate)}
                    onChange={(e) => {
                      const newDate = parseDateFromInput(e.target.value);
                      setStartDate(newDate);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Note: Week will start on the Monday of the selected week
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Filter Staff by Job Title</InputLabel>
                    <Select
                      value={selectedJobTitleFilter}
                      label="Filter Staff by Job Title"
                      onChange={(e) => setSelectedJobTitleFilter(e.target.value)}
                    >
                      {jobTitles.map(title => (
                        <MenuItem key={title} value={title}>
                          {title === 'all' ? 'All Job Titles' : title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Select Staff Members ({selectedStaff.length} selected)
                  </Typography>
                  
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: '300px', overflow: 'auto' }}>
                    <Grid container spacing={1}>
                      {filteredStaff.map((staff) => (
                        <Grid item xs={12} sm={6} md={4} key={staff._id}>
                          <Card 
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              borderColor: selectedStaff.some(s => s._id === staff._id) ? 'primary.main' : 'divider',
                              bgcolor: selectedStaff.some(s => s._id === staff._id) ? 'primary.50' : 'white'
                            }}
                            onClick={() => {
                              setSelectedStaff(prev => {
                                const exists = prev.some(s => s._id === staff._id);
                                if (exists) {
                                  return prev.filter(s => s._id !== staff._id);
                                } else {
                                  return [...prev, staff];
                                }
                              });
                            }}
                          >
                            <CardContent sx={{ p: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  checked={selectedStaff.some(s => s._id === staff._id)}
                                  size="small"
                                  readOnly
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {staff.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {staff.jobtitle} • {staff.ward || 'No Ward'}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      {selectedStaff.length} staff selected
                    </Typography>
                    
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleCreateRollingRoster}
                      disabled={selectedStaff.length === 0 || creating}
                      sx={{
                        bgcolor: '#1976d2',
                        '&:hover': {
                          bgcolor: '#1565c0'
                        }
                      }}
                    >
                      {creating ? 'Creating...' : 'Create Rolling Roster'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      )}
      
      
<Dialog open={updatePeriodDialogOpen} onClose={() => setUpdatePeriodDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Update Rolling Roster Period</DialogTitle>
  <DialogContent>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
      Update the current 6-week period for {selectedRoster?.employeeName}. 
      This will change when the "Generate Availabilities" button works from.
    </Typography>
    
    <TextField
      fullWidth
      label="New Period Start Date"
      type="date"
      value={newPeriodStartDate ? formatDateForInput(newPeriodStartDate) : ''}
      onChange={(e) => setNewPeriodStartDate(parseDateFromInput(e.target.value))}
      InputLabelProps={{ shrink: true }}
      sx={{ mt: 2 }}
    />
    
    {newPeriodStartDate && (
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2">
          New 6-week period will be:
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {newPeriodStartDate.toLocaleDateString()} - {
            new Date(new Date(newPeriodStartDate).setDate(newPeriodStartDate.getDate() + (6 * 7) - 1)).toLocaleDateString()
          }
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          The "Generate Availabilities" button will create availabilities for this period.
        </Typography>
      </Box>
    )}
    
    <Alert severity="info" sx={{ mt: 2 }}>
      <Typography variant="body2">
        Current period: {formatRosterPeriod(selectedRoster)}
      </Typography>
    </Alert>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setUpdatePeriodDialogOpen(false)}>Cancel</Button>
    <Button 
      onClick={handleUpdatePeriod} 
      variant="contained" 
      disabled={!newPeriodStartDate || updatingPeriod}
    >
      {updatingPeriod ? 'Updating...' : 'Update Period'}
    </Button>
  </DialogActions>
</Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create 6-Week Rolling Roster</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formatDateForInput(startDate)}
                onChange={(e) => setStartDate(parseDateFromInput(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Select Staff Members
              </Typography>
              
              <Autocomplete
                multiple
                options={filteredStaff}
                getOptionLabel={(option) => `${option.name} - ${option.jobtitle} (${option.ward || 'No Ward'})`}
                value={selectedStaff}
                onChange={(event, newValue) => {
                  setSelectedStaff(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Staff"
                    placeholder="Choose staff members..."
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.jobtitle} • {option.ward || 'No Ward'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateRollingRoster} 
            variant="contained" 
            disabled={selectedStaff.length === 0 || creating}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Availability Dialog - Use the actual AvailabilityDialog component */}
      <AvailabilityDialog
        open={availabilityDialogOpen}
        onClose={() => setAvailabilityDialogOpen(false)}
        onSave={handleSaveAvailability}
        dayData={selectedDayData}
        isEditing={true}
      />

      {/* Set Next Period Dialog */}
<Dialog open={nextPeriodDialogOpen} onClose={() => setNextPeriodDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Set Next Generation Period</DialogTitle>
  <DialogContent>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
      This will set when the next 6-week period should start for {selectedRoster?.employeeName}
    </Typography>
    
    <TextField
      fullWidth
      label="Next Period Start Date"
      type="date"
      value={nextStartDate ? formatDateForInput(nextStartDate) : ''}
      onChange={(e) => setNextStartDate(parseDateFromInput(e.target.value))}
      InputLabelProps={{ shrink: true }}
      sx={{ mt: 2 }}
    />
    
    {nextStartDate && (
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2">
          Next 6-week period will be:
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {nextStartDate.toLocaleDateString()} - {
            new Date(new Date(nextStartDate).setDate(nextStartDate.getDate() + (6 * 7) - 1)).toLocaleDateString()
          }
        </Typography>
      </Box>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setNextPeriodDialogOpen(false)}>Cancel</Button>
    <Button 
      onClick={handleSetNextGenerationDate} 
      variant="contained" 
      disabled={!nextStartDate || settingNextDate}
    >
      {settingNextDate ? 'Setting...' : 'Set Next Period'}
    </Button>
  </DialogActions>
</Dialog>

{/* Generate for Specific Period Dialog */}
<Dialog open={generatePeriodDialogOpen} onClose={() => setGeneratePeriodDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Generate Availabilities for Period</DialogTitle>
  <DialogContent>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
      Generate availabilities for a specific 6-week period starting from:
    </Typography>
    
    <TextField
      fullWidth
      label="Period Start Date"
      type="date"
      value={selectedPeriodStartDate ? formatDateForInput(selectedPeriodStartDate) : ''}
      onChange={(e) => setSelectedPeriodStartDate(parseDateFromInput(e.target.value))}
      InputLabelProps={{ shrink: true }}
      sx={{ mt: 2 }}
    />
    
    {selectedPeriodStartDate && (
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2">
          This will generate availabilities for:
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {selectedPeriodStartDate.toLocaleDateString()} - {
            new Date(new Date(selectedPeriodStartDate).setDate(selectedPeriodStartDate.getDate() + (6 * 7) - 1)).toLocaleDateString()
          }
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Using the template from {selectedRoster?.employeeName}'s rolling roster
        </Typography>
      </Box>
    )}
    
    {/* Show previously generated periods */}
    {selectedRoster?.generatedPeriods && selectedRoster.generatedPeriods.length > 0 && (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Previously Generated Periods:
        </Typography>
        {selectedRoster.generatedPeriods.slice().reverse().map((period, index) => (
          <Chip
            key={index}
            label={`${new Date(period.startDate).toLocaleDateString()} - ${new Date(period.endDate).toLocaleDateString()}`}
            size="small"
            sx={{ mr: 1, mb: 1 }}
            variant="outlined"
          />
        ))}
      </Box>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setGeneratePeriodDialogOpen(false)}>Cancel</Button>
    <Button 
      onClick={() => handleGenerateForPeriod(selectedPeriodStartDate)} 
      variant="contained" 
      disabled={!selectedPeriodStartDate || generatingPeriod}
    >
      {generatingPeriod ? 'Generating...' : 'Generate for Period'}
    </Button>
  </DialogActions>
</Dialog>
      
{/* Print Dialog */}
<Dialog 
  open={printDialogOpen} 
  onClose={() => setPrintDialogOpen(false)} 
  maxWidth="lg" 
  fullWidth
  fullScreen={isPrinting}
>
  <DialogTitle>
    Print 6-Week Rolling Schedule
    <Typography variant="subtitle2" color="textSecondary">
      {selectedRoster?.employeeName} • {formatRosterPeriod(selectedRoster)}
    </Typography>
  </DialogTitle>
  <DialogContent dividers>
    {selectedRoster && (
      <Box id="print-content">
        <RollingRosterPrintView 
          roster={selectedRoster} 
          startDate={selectedRoster.startDate} 
        />
      </Box>
    )}
  </DialogContent>
  <DialogActions sx={{ justifyContent: 'space-between' }}>
    <Box>
      <Typography variant="caption" color="textSecondary">
        Tip: Use landscape orientation for best printing results
      </Typography>
    </Box>
    <Box>
      <Button onClick={() => setPrintDialogOpen(false)}>
        Cancel
      </Button>
      <Button
        variant="contained"
        startIcon={<Print />}
        onClick={() => {
          setIsPrinting(true);
          setTimeout(() => {
            window.print();
            setIsPrinting(false);
            setPrintDialogOpen(false);
          }, 100);
        }}
        sx={{ ml: 1 }}
      >
        Print
      </Button>
     
      {/*
      <Button
        variant="outlined"
        startIcon={<Download />}
        onClick={() => {
          // Export as PDF functionality (optional)
          handleExportAsPDF();
        }}
        sx={{ ml: 1 }}
      >
        Export PDF
      </Button>
      */}

    </Box>
  </DialogActions>
</Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RollingRosterView;